import { Box, Divider, SxProps } from '@mui/material';
import {
  CandidateEmailCorrespondence,
  CorrespondenceStatus
} from 'services/candidate/types';
import { useMutation } from 'services/api/hooks';
import {
  senEmailCorrespondence,
  updateCandidateEmailCorrespondence
} from 'services/candidate/api';
import { useToast, ToastType } from 'services/toast';
import { EmailCorrespondenceMessage } from './EmailCorrespondenceMessage';
import { useState } from 'react';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  mb: 2
};

type Props = {
  emailCorrespondences: CandidateEmailCorrespondence[] | undefined | null;
  setEmailCorrespondences: (
    emailCorrespondence: CandidateEmailCorrespondence[]
  ) => void;
  readOnly?: boolean;
};

export const EmailCorrespondencesTab: React.FC<Props> = ({
  emailCorrespondences,
  setEmailCorrespondences,
  readOnly = false
}) => {
  const [isSending, setIsSending] = useState(false);
  const { showToast } = useToast();

  const { mutate: updateStatus } = useMutation(
    updateCandidateEmailCorrespondence,
    {
      disableSuccessToast: true
    }
  );

  const pauseEmailCorrespondence = async (id: string) => {
    if (emailCorrespondences) {
      const data = await updateStatus({
        id,
        status: CorrespondenceStatus.PAUSED
      });

      setEmailCorrespondences([data]);
    }
  };

  const restoreEmailCorrespondence = async (id: string) => {
    if (emailCorrespondences) {
      const data = await updateStatus({
        id,
        status: CorrespondenceStatus.SCHEDULED
      });

      setEmailCorrespondences([data]);
    }
  };

  const triggerSend = async (id: string) => {
    try {
      setIsSending(true);

      if (emailCorrespondences) {
        await senEmailCorrespondence({
          candidateEmailCorrespondenceId: id
        });

        const emailCorrespondence = emailCorrespondences.find(
          (item) => item.id === id
        );

        if (emailCorrespondence) {
          setEmailCorrespondences([
            {
              ...emailCorrespondence,
              status: CorrespondenceStatus.SENT
            }
          ]);
        }
      }
    } catch (error) {
      showToast({ message: 'Failed to send email', type: ToastType.Error });
    } finally {
      setIsSending(false);
    }
  };

  if (!emailCorrespondences?.length) {
    return null;
  }

  return (
    <>
      {emailCorrespondences?.map((emailCorrespondence, index) => (
        <Box sx={container} key={emailCorrespondence.id}>
          <EmailCorrespondenceMessage
            key={emailCorrespondence.id}
            emailCorrespondence={emailCorrespondence}
            onPauseEmailCorrespondenceClicked={pauseEmailCorrespondence}
            onTriggerSendClicked={triggerSend}
            onRestoreEmailCorrespondenceClicked={restoreEmailCorrespondence}
            isSending={isSending}
            readOnly={readOnly}
          />
          {index !== emailCorrespondences?.length - 1 && <Divider />}
        </Box>
      ))}
    </>
  );
};
